import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import pacientesService, { Consulta, UpdateConsultaDto } from '../../services/pacientesService';
import {
  ArrowLeft as ArrowLeftIcon,
  Save as SaveIcon,
  Printer as PrinterIcon,
  RefreshCw as RefreshIcon,
  Plus as PlusIcon,
  Trash2 as TrashIcon,
  FileText as DocumentTextIcon,
} from 'lucide-react';

interface ConsultaFormState {
  fecha: string;
  temperatura: string;
  peso: string;
  estatura: string;
  revisionFisica: string;
  observaciones: string;
  diagnosticos: { diagnostico: string }[];
  medicamentos: {
    medicamento: string;
    dosis: string;
    frecuencia: string;
    duracion: string;
    indicaciones: string;
  }[];
}

const emptyFormState: ConsultaFormState = {
  fecha: '',
  temperatura: '',
  peso: '',
  estatura: '',
  revisionFisica: '',
  observaciones: '',
  diagnosticos: [],
  medicamentos: [],
};

const formatDateTimeLocal = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const tzOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
};

const parseNumber = (value: string) => {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

export default function ConsultaEditPage() {
  const navigate = useNavigate();
  const { consultaId } = useParams();
  const numericConsultaId = Number(consultaId);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [consulta, setConsulta] = useState<Consulta | null>(null);
  const [formData, setFormData] = useState<ConsultaFormState>(emptyFormState);

  useEffect(() => {
    if (!numericConsultaId) {
      toast.error('Consulta no válida');
      navigate('/pacientes');
      return;
    }

    loadConsulta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericConsultaId]);

  const loadConsulta = async () => {
    try {
      setLoading(true);
      const data = await pacientesService.getConsulta(numericConsultaId);
      setConsulta(data);
      setFormData({
        fecha: formatDateTimeLocal(data.fecha),
        temperatura: data.temperatura?.toString() ?? '',
        peso: data.peso?.toString() ?? '',
        estatura: data.estatura?.toString() ?? '',
        revisionFisica: data.revisionFisica ?? '',
        observaciones: data.observaciones ?? '',
        diagnosticos: data.diagnosticos?.map((d) => ({ diagnostico: d.diagnostico })) ?? [],
        medicamentos:
          data.medicamentos?.map((m) => ({
            medicamento: m.medicamento ?? '',
            dosis: m.dosis ?? '',
            frecuencia: m.frecuencia ?? '',
            duracion: m.duracion ?? '',
            indicaciones: m.indicaciones ?? '',
          })) ?? [],
      });
    } catch (error) {
      console.error('Error al cargar consulta:', error);
      toast.error('No fue posible cargar la consulta');
      navigate('/pacientes');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const addDiagnostico = () => {
    setFormData((prev) => ({
      ...prev,
      diagnosticos: [...prev.diagnosticos, { diagnostico: '' }],
    }));
  };

  const updateDiagnostico = (index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      diagnosticos: prev.diagnosticos.map((diag, i) => (i === index ? { diagnostico: value } : diag)),
    }));
  };

  const removeDiagnostico = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      diagnosticos: prev.diagnosticos.filter((_, i) => i !== index),
    }));
  };

  const addMedicamento = () => {
    setFormData((prev) => ({
      ...prev,
      medicamentos: [
        ...prev.medicamentos,
        { medicamento: '', dosis: '', frecuencia: '', duracion: '', indicaciones: '' },
      ],
    }));
  };

  const updateMedicamento = (index: number, field: keyof ConsultaFormState['medicamentos'][number], value: string) => {
    setFormData((prev) => ({
      ...prev,
      medicamentos: prev.medicamentos.map((med, i) => (i === index ? { ...med, [field]: value } : med)),
    }));
  };

  const removeMedicamento = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      medicamentos: prev.medicamentos.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const diagnosticosLimpios = formData.diagnosticos
      .map((d) => ({ diagnostico: d.diagnostico.trim() }))
      .filter((d) => d.diagnostico.length > 0);

    if (diagnosticosLimpios.length === 0) {
      toast.error('Debe agregar al menos un diagnóstico.');
      return;
    }

    const medicamentosLimpios = formData.medicamentos
      .map((m) => ({
        medicamento: m.medicamento.trim(),
        dosis: m.dosis.trim(),
        frecuencia: m.frecuencia.trim(),
        duracion: m.duracion.trim(),
        indicaciones: m.indicaciones.trim() || undefined,
      }))
      .filter((m) => m.medicamento.length > 0);

    const payload: UpdateConsultaDto = {
      fecha: formData.fecha ? new Date(formData.fecha).toISOString() : undefined,
      temperatura: parseNumber(formData.temperatura),
      peso: parseNumber(formData.peso),
      estatura: parseNumber(formData.estatura),
      revisionFisica: formData.revisionFisica.trim() || undefined,
      observaciones: formData.observaciones.trim() || undefined,
      diagnosticos: diagnosticosLimpios,
      medicamentos: medicamentosLimpios,
    };

    try {
      setSaving(true);
      const updatedConsulta = await pacientesService.updateConsulta(numericConsultaId, payload);
      setConsulta(updatedConsulta);
      toast.success('Consulta actualizada correctamente');

      const shouldPrint = window.confirm('Consulta guardada. ¿Deseas imprimir la receta actualizada?');
      if (shouldPrint) {
        navigate(`/pacientes/consultas/${numericConsultaId}/receta`);
      } else if (updatedConsulta.pacienteId) {
        navigate(`/pacientes/${updatedConsulta.pacienteId}/historial`);
      } else {
        navigate('/pacientes');
      }
    } catch (error) {
      console.error('Error al actualizar consulta:', error);
      toast.error('No fue posible actualizar la consulta');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!consulta) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() =>
              consulta.pacienteId ? navigate(`/pacientes/${consulta.pacienteId}/historial`) : navigate('/pacientes')
            }
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-6 h-6 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <RefreshIcon className="w-7 h-7 text-amber-600" />
              Editar Consulta
            </h1>
            {consulta.paciente && (
              <p className="text-gray-600 mt-1">
                Paciente: <span className="font-semibold">{consulta.paciente.nombre}</span>
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => navigate(`/pacientes/consultas/${numericConsultaId}/receta`)}
            className="flex items-center gap-2 px-3 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
          >
            <PrinterIcon className="w-4 h-4" />
            Ver Receta
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <DocumentTextIcon className="w-4 h-4" />
            Imprimir Vista
          </button>
        </div>
      </div>

      {/* Formulario */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Datos Vitales</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fecha y Hora</label>
              <input
                type="datetime-local"
                name="fecha"
                value={formData.fecha}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Temperatura (°C)</label>
              <input
                type="number"
                step="0.1"
                name="temperatura"
                value={formData.temperatura}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="36.5"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Peso (kg)</label>
              <input
                type="number"
                step="0.01"
                name="peso"
                value={formData.peso}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="15.5"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Estatura (cm)</label>
              <input
                type="number"
                step="0.01"
                name="estatura"
                value={formData.estatura}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="105.5"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Revisión Física</h2>
          <textarea
            name="revisionFisica"
            value={formData.revisionFisica}
            onChange={handleInputChange}
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Descripción detallada de la revisión física..."
          />
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Diagnósticos <span className="text-red-500">*</span>
            </h2>
            <button
              type="button"
              onClick={addDiagnostico}
              className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Agregar
            </button>
          </div>
          <div className="space-y-3">
            {formData.diagnosticos.map((diag, index) => (
              <div key={`diagnostico-${index}`} className="flex gap-2">
                <input
                  type="text"
                  value={diag.diagnostico}
                  onChange={(e) => updateDiagnostico(index, e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Diagnóstico..."
                  required
                />
                <button
                  type="button"
                  onClick={() => removeDiagnostico(index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  aria-label="Eliminar diagnóstico"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            ))}
            {formData.diagnosticos.length === 0 && (
              <p className="text-gray-500 text-sm">No hay diagnósticos agregados</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Medicamentos Recetados</h2>
            <button
              type="button"
              onClick={addMedicamento}
              className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Agregar
            </button>
          </div>
          <div className="space-y-4">
            {formData.medicamentos.map((med, index) => (
              <div key={`medicamento-${index}`} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-sm font-medium text-gray-700">Medicamento {index + 1}</h3>
                  <button
                    type="button"
                    onClick={() => removeMedicamento(index)}
                    className="text-red-600 hover:bg-red-50 p-1 rounded transition-colors"
                    aria-label="Eliminar medicamento"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={med.medicamento}
                    onChange={(e) => updateMedicamento(index, 'medicamento', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Nombre del medicamento"
                    required
                  />
                  <input
                    type="text"
                    value={med.dosis}
                    onChange={(e) => updateMedicamento(index, 'dosis', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Dosis (ej: 5 ml)"
                  />
                  <input
                    type="text"
                    value={med.frecuencia}
                    onChange={(e) => updateMedicamento(index, 'frecuencia', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Frecuencia (ej: cada 8 horas)"
                  />
                  <input
                    type="text"
                    value={med.duracion}
                    onChange={(e) => updateMedicamento(index, 'duracion', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Duración (ej: 7 días)"
                  />
                  <textarea
                    value={med.indicaciones}
                    onChange={(e) => updateMedicamento(index, 'indicaciones', e.target.value)}
                    className="md:col-span-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Indicaciones adicionales..."
                    rows={2}
                  />
                </div>
              </div>
            ))}
            {formData.medicamentos.length === 0 && (
              <p className="text-gray-500 text-sm">No hay medicamentos agregados</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Observaciones Generales</h2>
          <textarea
            name="observaciones"
            value={formData.observaciones}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Observaciones adicionales, recomendaciones, etc..."
          />
        </div>

        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => (consulta.pacienteId ? navigate(`/pacientes/${consulta.pacienteId}/historial`) : navigate('/pacientes'))}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <SaveIcon className="w-5 h-5" />
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </form>
    </div>
  );
}
